import type { ApiRoute } from '@socialfy/api-contract';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { t } from '@/i18n/t';
import type { CallOptions } from './client';
import { ApiOffline, ApiProblem } from './problem';
import { useApiClient } from './provider';
import type { CallInput, InputArgs, InputOptional, ResponseData } from './types';

const MAX_RETRIES = 2;

/**
 * Wiederholungsregel: 4xx nie (auch 429 „limit_reached“ nicht, V12-01), 5xx und fehlende Verbindung
 * höchstens zweimal, Vertragsfehler und fehlende Schutzmarke nie.
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_RETRIES) {
    return false;
  }
  if (error instanceof ApiOffline) {
    return true;
  }
  return error instanceof ApiProblem && error.status >= 500;
}

export type QueryClientOptions = {
  /** Wartezeit vor einer Wiederholung in ms (Tests: 0); Standard: 1 s, 2 s, höchstens 8 s. */
  retryDelay?: number;
  /** Wird für jede Problem-Antwort aufgerufen (z. B. Hinweis „Im Demo-Modus nicht möglich.“). */
  onProblem?: (problem: ApiProblem) => void;
};

const backoff = (attempt: number): number => Math.min(1000 * 2 ** attempt, 8000);

/**
 * Zwischenspeicher der Anwendung: 30 s frisch, `networkMode: 'always'` (offline entsteht ein Fehler statt
 * eines stillen Wartens, die letzten Daten bleiben sichtbar), Handlungen werden nie von allein wiederholt.
 */
export function createQueryClient(options: QueryClientOptions = {}): QueryClient {
  const { retryDelay, onProblem } = options;
  const report = (error: unknown) => {
    if (error instanceof ApiProblem) {
      onProblem?.(error);
    }
  };
  return new QueryClient({
    queryCache: new QueryCache({ onError: report }),
    mutationCache: new MutationCache({ onError: report }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        networkMode: 'always',
        retry: shouldRetry,
        retryDelay: retryDelay ?? backoff,
      },
      mutations: { retry: false, networkMode: 'always' },
    },
  });
}

/** Optionen einer lesenden Abfrage (letztes Argument von `useApiQuery`). */
export type ApiQueryOptions<R extends ApiRoute> = {
  /** `false`: nicht laden (z. B. bis eine Auswahl feststeht). Standard: `true`. */
  enabled?: boolean;
  /**
   * Wiederholt die Abfrage im Takt (in ms), z. B. solange ein Vorgang läuft. Als Funktion bekommt sie die
   * zuletzt geladenen Daten (`undefined` vor der ersten Antwort) und liefert die Wartezeit oder `false`, um
   * aufzuhören. Im Hintergrund-Tab pausiert der Takt.
   */
  refetchInterval?: number | false | ((data: ResponseData<R> | undefined) => number | false);
  /** Wie lange nicht mehr benutzte Daten im Zwischenspeicher bleiben (ms); `0` entfernt sie beim Aushängen. */
  gcTime?: number;
};

/** Lesende Abfrage einer Route; der Schlüssel besteht aus `operationId` und Eingabe. */
export function useApiQuery<R extends ApiRoute>(
  route: R,
  ...args: [...InputArgs<R>, options?: ApiQueryOptions<R>]
): UseQueryResult<ResponseData<R>, Error> {
  const client = useApiClient();
  const [input, options] = args as unknown as [
    CallInput<R> | undefined,
    ApiQueryOptions<R> | undefined,
  ];
  const interval = options?.refetchInterval;
  return useQuery<ResponseData<R>, Error>({
    queryKey: [`${route.meta.module}.${route.meta.name}`, input ?? null],
    queryFn: ({ signal }) =>
      (client.call as (r: R, i: unknown, o: CallOptions) => Promise<ResponseData<R>>)(
        route,
        input,
        { signal },
      ),
    enabled: options?.enabled ?? true,
    // TanStack Query 5 übergibt dem Rückruf das Query-Objekt; die Seiten sehen nur die Daten.
    ...(interval === undefined
      ? {}
      : {
          refetchInterval:
            typeof interval === 'function'
              ? (query: { state: { data: ResponseData<R> | undefined } }) =>
                  interval(query.state.data)
              : interval,
        }),
    ...(options?.gcTime === undefined ? {} : { gcTime: options.gcTime }),
  });
}

/** Ist das Gerät online? Folgt den Ereignissen `online` und `offline`. */
export function useOnline(): boolean {
  const [online, setOnline] = useState<boolean>(() => globalThis.navigator?.onLine ?? true);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

type Variables<R extends ApiRoute> =
  InputOptional<R> extends true ? CallInput<R> | undefined : CallInput<R>;

/**
 * Handlung (schreibende Route). Jede Nutzerhandlung bekommt einen eigenen Idempotenzschlüssel; die Handlung
 * wird nie von allein wiederholt (nur der Client selbst wiederholt einmal nach abgelehnter Schutzmarke,
 * mit demselben Schlüssel). Ohne Verbindung wird gar nicht erst gesendet.
 */
export function useApiMutation<R extends ApiRoute>(
  route: R,
): UseMutationResult<ResponseData<R>, Error, Variables<R>> {
  const client = useApiClient();
  return useMutation<ResponseData<R>, Error, Variables<R>>({
    mutationFn: (input) => {
      if (!globalThis.navigator.onLine) {
        return Promise.reject(new ApiOffline());
      }
      return (client.call as (r: R, i: unknown, o: CallOptions) => Promise<ResponseData<R>>)(
        route,
        input,
        { idempotencyKey: crypto.randomUUID() },
      );
    },
  });
}

/** Ergebnis von `useGuardedMutation`: die Handlung samt `run` und `busyReason`. */
export type GuardedMutation<R extends ApiRoute> = UseMutationResult<
  ResponseData<R>,
  Error,
  Variables<R>
> & {
  /**
   * Sendet die Handlung, außer eine läuft schon: dann geschieht nichts und das Ergebnis ist `null` (Schutz gegen
   * Doppelklick und doppeltes Enter; jeder Aufruf hätte sonst einen neuen Idempotenzschlüssel). Bei Erfolg die
   * Antwortdaten; bei einem Fehler wird abgelehnt (`ApiProblem`, `ApiOffline`, …), er steht zugleich in `error`.
   */
  run: (...args: InputArgs<R>) => Promise<ResponseData<R> | null>;
  /** Grund für `disabledReason` des auslösenden Knopfes, solange die Anfrage läuft, sonst `undefined`. */
  busyReason: string | undefined;
};

/**
 * `useApiMutation` mit Sperre gegen Doppelauslösung. Die Sperre gilt sofort (Referenz statt Render-Zustand), also
 * auch für zwei Aufrufe im selben Takt, bevor die Oberfläche neu gezeichnet ist. Jede schreibende Handlung der
 * Seiten läuft darüber.
 */
export function useGuardedMutation<R extends ApiRoute>(route: R): GuardedMutation<R> {
  const mutation = useApiMutation(route);
  const inFlight = useRef(false);
  const { mutateAsync, isPending } = mutation;
  const run = useCallback(
    async (...args: InputArgs<R>): Promise<ResponseData<R> | null> => {
      if (inFlight.current) {
        return null;
      }
      inFlight.current = true;
      try {
        return (await mutateAsync(args[0] as Variables<R>)) as unknown as ResponseData<R> | null;
      } finally {
        inFlight.current = false;
      }
    },
    [mutateAsync],
  );
  return { ...mutation, run, busyReason: isPending ? t('ui.saving') : undefined };
}
